import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { 
  ArrowLeft, Phone, Search, X, Sparkles, MapPin, 
  Trophy, Star, TrendingUp, ShieldCheck, CheckCircle2, ChevronRight 
} from "lucide-react";
import BookingModal from "@/components/BookingModal";
import CelebrationIntroModal from "@/components/CelebrationIntroModal";
import "../CutoffExplorer.css";

const PHONE = "+919309553235";

const PRIMARY_CATEGORIES = [
  { id: "OPEN", label: "OPEN" },
  { id: "OBC", label: "OBC" },
  { id: "EWS", label: "EWS" },
  { id: "SEBC", label: "SEBC" },
  { id: "SC", label: "SC" },
  { id: "ST", label: "ST" },
  { id: "VJA", label: "VJ / VJA" },
  { id: "NTB", label: "NT-B (NT1)" },
  { id: "NTC", label: "NT-C (NT2)" },
  { id: "NTD", label: "NT-D (NT3)" }
];

const SPECIAL_CATEGORIES = [
  { id: "DEF 1", label: "DEF 1" },
  { id: "DEF 2", label: "DEF 2" },
  { id: "DEF 3", label: "DEF 3" },
  { id: "HILLY GENERAL", label: "HILLY GENERAL" },
  { id: "HILLY OBC", label: "HILLY OBC" },
  { id: "HILLY EWS", label: "HILLY EWS" },
  { id: "HILLY SC", label: "HILLY SC" },
  { id: "HILLY ST", label: "HILLY ST" },
  { id: "PWD OPEN", label: "PWD OPEN", isNoGender: true },
  { id: "PWD OBC", label: "PWD OBC", isNoGender: true },
  { id: "PWD EWS", label: "PWD EWS", isNoGender: true },
  { id: "PWD SC", label: "PWD SC", isNoGender: true },
  { id: "PWD ST", label: "PWD ST", isNoGender: true },
  { id: "MKB", label: "MKB (Border Area)" },
  { id: "ORPHAN", label: "ORPHAN", isNoGender: true },
  { id: "I.Q.", label: "Institutional Quota (I.Q.)", isNoGender: true },
  { id: "MINORITY OPEN", label: "Minority Open" }
];

function resolveStateCategoryKey(category, gender) {
  if (category === "ORPHAN" || category.startsWith("PWD") || category === "I.Q." || category === "I.Q. MINORITY") {
    return category;
  }
  if (category === "MKB") {
    return gender === "W" ? "MKB WOMEN" : "MKB GENERAL";
  }
  if (category === "HILLY GENERAL") {
    return gender === "W" ? "HILLY GENERAL W" : "HILLY GENERAL";
  }
  return `${category} ${gender}`;
}

export const DEFAULT_SHEET_DETAILS = [
  // --- ROUND 3 (LATEST) ---
  {
    id: "mbbs_r3",
    name: "85% MBBS R3",
    round: 3,
    title: "Maharashtra MBBS (Round 3) — 85% State Quota",
    type: "state",
    badge: "State Quota",
    course: "MBBS",
    group: "Maharashtra State Quota (85%)"
  },
  {
    id: "bds_r3",
    name: "85% BDS R3",
    round: 3,
    title: "Maharashtra BDS (Round 3) — 85% State Quota",
    type: "state",
    badge: "State Quota",
    course: "BDS",
    group: "Maharashtra State Quota (85%)"
  },
  {
    id: "aiims_r3",
    name: "AIIMS R3",
    round: 3,
    title: "AIIMS & Central Institutes (Round 3)",
    type: "aiims",
    badge: "Central 100%",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "deemed_mbbs_r3",
    name: "DEEMED MBBS 3",
    round: 3,
    title: "Deemed Universities MBBS (Round 3)",
    type: "deemed",
    badge: "Deemed / All India",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "deemed_bds_r3",
    name: "DEEMED BDS 3",
    round: 3,
    title: "Deemed Universities BDS (Round 3)",
    type: "deemed",
    badge: "Deemed / All India",
    course: "BDS",
    group: "Central & Deemed Quotas"
  },

  // --- ROUND 2 ---
  {
    id: "mbbs_r2",
    name: "85% MBBS R2",
    round: 2,
    title: "Maharashtra MBBS (Round 2) — 85% State Quota",
    type: "state",
    badge: "State Quota",
    course: "MBBS",
    group: "Maharashtra State Quota (85%)"
  },
  {
    id: "bds_r2",
    name: "85% BDS R2",
    round: 2,
    title: "Maharashtra BDS (Round 2) — 85% State Quota",
    type: "state",
    badge: "State Quota",
    course: "BDS",
    group: "Maharashtra State Quota (85%)"
  },
  {
    id: "bams_r2",
    name: "BAMS R2 2026",
    round: 2,
    title: "Maharashtra BAMS (Round 2) — 85% State Quota",
    type: "state",
    badge: "AYUSH State",
    course: "BAMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "bhms_r2",
    name: "BHMS R2 2026",
    round: 2,
    title: "Maharashtra BHMS (Round 2) — 85% State Quota",
    type: "state",
    badge: "AYUSH State",
    course: "BHMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "aiq_bams_r2",
    name: "AIQ 15% Private BAMS R2",
    round: 2,
    title: "15% AIQ Private BAMS (Round 2) — All India Quota",
    type: "aiq_bams",
    badge: "AYUSH AIQ 15%",
    course: "BAMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "aiq_bhms_r2",
    name: "AIQ 15% Private BHMS R2",
    round: 2,
    title: "15% AIQ Private BHMS (Round 2) — All India Quota",
    type: "aiq_bams",
    badge: "AYUSH AIQ 15%",
    course: "BHMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "aiims_r2",
    name: "AIIMS R2",
    round: 2,
    title: "AIIMS & JIPMER (Round 2)",
    type: "aiims",
    badge: "Central 100%",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "deemed_mbbs_r2",
    name: "DEEMED MBBS 2",
    round: 2,
    title: "Deemed Universities MBBS (Round 2)",
    type: "deemed",
    badge: "Deemed / All India",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "deemed_bds_r2",
    name: "DEEMED BDS 2",
    round: 2,
    title: "Deemed Universities BDS (Round 2)",
    type: "deemed",
    badge: "Deemed / All India",
    course: "BDS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "kea_mbbs_r2",
    name: "KEA MBBS R2",
    round: 2,
    title: "Karnataka KEA MBBS (Round 2)",
    type: "kea",
    badge: "KEA Karnataka",
    course: "MBBS",
    group: "Karnataka KEA"
  },

  // --- ROUND 1 ---
  {
    id: "mbbs_r1",
    name: "MBBS R1 2026",
    round: 1,
    title: "Maharashtra MBBS (Round 1) — 85% State Quota",
    type: "state",
    badge: "State Quota",
    course: "MBBS",
    group: "Maharashtra State Quota (85%)"
  },
  {
    id: "bds_r1",
    name: "BDS R1 2026",
    round: 1,
    title: "Maharashtra BDS (Round 1) — 85% State Quota",
    type: "state",
    badge: "State Quota",
    course: "BDS",
    group: "Maharashtra State Quota (85%)"
  },
  {
    id: "bams_r1",
    name: "BAMS R1 2026",
    round: 1,
    title: "Maharashtra BAMS (Round 1) — 85% State Quota",
    type: "state",
    badge: "AYUSH State",
    course: "BAMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "bhms_r1",
    name: "BHMS R1 2026",
    round: 1,
    title: "Maharashtra BHMS (Round 1) — 85% State Quota",
    type: "state",
    badge: "AYUSH State",
    course: "BHMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "aiq_bams_r1",
    name: "AIQ 15% Private BAMS R1",
    round: 1,
    title: "15% AIQ Private BAMS (Round 1) — All India Quota",
    type: "aiq_bams",
    badge: "AYUSH AIQ 15%",
    course: "BAMS",
    group: "AYUSH Courses (BAMS & BHMS)"
  },
  {
    id: "aiims_r1",
    name: "AIIMS R1",
    round: 1,
    title: "AIIMS (Round 1) — Central 100%",
    type: "aiims",
    badge: "Central 100%",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "aiq_mbbs",
    name: "AIQ 15% MBBS R1",
    round: 1,
    title: "15% AIQ GMCs (Round 1) — MCC AIQ",
    type: "aiq",
    badge: "MCC AIQ",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "deemed_mbbs",
    name: "DEEMED MBBS",
    round: 1,
    title: "Deemed Universities MBBS (Round 1)",
    type: "deemed",
    badge: "Deemed / All India",
    course: "MBBS",
    group: "Central & Deemed Quotas"
  },
  {
    id: "deemed_bds",
    name: "DEEMED BDS",
    round: 1,
    title: "Deemed Universities BDS (Round 1)",
    type: "deemed",
    badge: "Deemed / All India",
    course: "BDS",
    group: "Central & Deemed Quotas"
  }
];

export default function Neet2026CutoffExplorer() {
  const navigate = useNavigate();
  const [metadata, setMetadata] = useState({
    sheets: DEFAULT_SHEET_DETAILS.map((s) => s.name),
    sheetDetails: DEFAULT_SHEET_DETAILS
  });

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showHandbookModal, setShowHandbookModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(true);

  const handleCelebrationClose = () => {
    setShowCelebration(false);
  };

  // Round 3 is the default latest round
  const [selectedRound, setSelectedRound] = useState(() => {
    const saved = localStorage.getItem("ame_neet2026_selected_round");
    const num = Number(saved);
    return [1, 2, 3].includes(num) ? num : 3;
  });

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("ame_neet2026_cutoff_filters");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.sheetName) return parsed;
      } catch (e) {
        console.error("Failed to parse saved filters", e);
      }
    }
    return {
      sheetName: "85% MBBS R3",
      category: "OPEN",
      gender: "G"
    };
  });

  const [rawSheetData, setRawSheetData] = useState(null);
  const [tableData, setTableData] = useState({ head: [], body: [], cards: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourseFilter, setSelectedCourseFilter] = useState("ALL");

  // Fetch Metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const res = await api.get("/neet-2026/metadata");
        if (res.data && res.data.sheets) {
          const allSheets = res.data.sheets;
          const sheetsList = allSheets.map((s) => s.name);
          setMetadata({ sheets: sheetsList, sheetDetails: allSheets });

          const savedRound = localStorage.getItem("ame_neet2026_selected_round");
          const initRound = [1, 2, 3].includes(Number(savedRound)) ? Number(savedRound) : 3;
          setSelectedRound(initRound);

          const saved = localStorage.getItem("ame_neet2026_cutoff_filters");
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              const found = allSheets.find((s) => s.name === parsed.sheetName);
              if (found && (found.round || 3) === initRound) {
                setFilters(parsed);
                fetchSheet(parsed.sheetName, parsed, initRound);
                return;
              }
            } catch (e) {
              console.error("Failed to load saved filters", e);
            }
          }

          // Pick default sheet for this round
          const roundSheets = allSheets.filter((s) => (s.round || 3) === initRound);
          const defaultSheet = roundSheets.length > 0 ? roundSheets[0].name : "85% MBBS R3";
          const initialFilters = {
            sheetName: defaultSheet,
            category: defaultSheet.includes("AIIMS") ? "ALL CATEGORY" : "OPEN",
            gender: "G"
          };
          setFilters(initialFilters);
          fetchSheet(defaultSheet, initialFilters, initRound);
        }
      } catch (err) {
        console.error("Error fetching NEET 2026 metadata:", err);
      }
    };
    fetchMetadata();
  }, []);

  // Fetch sheet data from API
  const fetchSheet = async (sheetName, activeFilters, roundNum) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/neet-2026/sheet-data?sheet=${encodeURIComponent(sheetName)}`);
      if (!res.data || !res.data.success) {
        setError("No data available for this sheet.");
        setRawSheetData(null);
        setTableData({ head: [], body: [], cards: [] });
        return;
      }
      setRawSheetData(res.data);
      processTableData(res.data, activeFilters || filters, roundNum || selectedRound);
    } catch (err) {
      setError("Failed to load 2026 cutoff data.");
      setRawSheetData(null);
    } finally {
      setLoading(false);
    }
  };

  // Re-process table data in real-time whenever rawSheetData, filters, or selectedRound changes
  useEffect(() => {
    if (rawSheetData) {
      processTableData(rawSheetData, filters, selectedRound);
    }
  }, [filters.category, filters.gender, rawSheetData, selectedRound]);

  // Handle Course Pill Selection (MBBS, BDS, BAMS, BHMS)
  const handleCourseFilterChange = (course) => {
    setSelectedCourseFilter(course);
    if (course === "ALL") return;

    const allSheets = metadata.sheetDetails || [];

    // If BAMS or BHMS is selected on Round 3, automatically switch to Round 2 (where AYUSH cutoffs exist!)
    if ((course === "BAMS" || course === "BHMS") && selectedRound === 3) {
      handleRoundChange(2, course);
      return;
    }

    const currentRoundSheets = allSheets.filter((s) => (s.round || 3) === selectedRound);
    const match = currentRoundSheets.find((s) => (s.course || "").toUpperCase() === course.toUpperCase());

    if (match && match.name !== filters.sheetName) {
      const updated = {
        ...filters,
        sheetName: match.name,
        category: match.name.includes("AIIMS") ? "ALL CATEGORY" : (filters.category || "OPEN")
      };
      setFilters(updated);
      localStorage.setItem("ame_neet2026_cutoff_filters", JSON.stringify(updated));
      fetchSheet(match.name, updated, selectedRound);
    }
  };

  // Handle Round Change (Round 3, Round 2, Round 1) with intelligent sheet mapping
  const handleRoundChange = (newRound, forceCourse) => {
    if (newRound === selectedRound && !forceCourse) return;
    setSelectedRound(newRound);
    localStorage.setItem("ame_neet2026_selected_round", String(newRound));

    const allSheets = metadata.sheetDetails || [];
    const targetSheets = allSheets.filter((s) => (s.round || 3) === newRound);
    if (targetSheets.length === 0) return;

    const currentSheetName = filters.sheetName;
    const currentCourse = forceCourse || (metadata.sheetDetails.find((s) => s.name === currentSheetName)?.course || "MBBS");
    let targetSheetName = targetSheets[0].name;

    // Smart Course-Based Cross-Round Mapping
    if (currentCourse === "MBBS") {
      if (currentSheetName.includes("AIIMS")) {
        targetSheetName = newRound === 3 ? "AIIMS R3" : (newRound === 2 ? "AIIMS R2" : "AIIMS R1");
      } else if (currentSheetName.includes("DEEMED")) {
        targetSheetName = newRound === 3 ? "DEEMED MBBS 3" : (newRound === 2 ? "DEEMED MBBS 2" : "DEEMED MBBS");
      } else if (currentSheetName.includes("KEA")) {
        targetSheetName = newRound === 2 ? "KEA MBBS R2" : (newRound === 3 ? "85% MBBS R3" : "MBBS R1 2026");
      } else {
        targetSheetName = newRound === 3 ? "85% MBBS R3" : (newRound === 2 ? "85% MBBS R2" : "MBBS R1 2026");
      }
    } else if (currentCourse === "BDS") {
      if (currentSheetName.includes("DEEMED")) {
        targetSheetName = newRound === 3 ? "DEEMED BDS 3" : (newRound === 2 ? "DEEMED BDS 2" : "DEEMED BDS");
      } else {
        targetSheetName = newRound === 3 ? "85% BDS R3" : (newRound === 2 ? "85% BDS R2" : "BDS R1 2026");
      }
    } else if (currentCourse === "BAMS") {
      if (newRound === 2) {
        targetSheetName = currentSheetName.includes("AIQ") ? "AIQ 15% Private BAMS R2" : "BAMS R2 2026";
      } else if (newRound === 1) {
        targetSheetName = currentSheetName.includes("AIQ") ? "AIQ 15% Private BAMS R1" : "BAMS R1 2026";
      } else {
        // Round 3 has no BAMS yet, fallback to MBBS
        targetSheetName = "85% MBBS R3";
      }
    } else if (currentCourse === "BHMS") {
      if (newRound === 2) {
        targetSheetName = currentSheetName.includes("AIQ") ? "AIQ 15% Private BHMS R2" : "BHMS R2 2026";
      } else if (newRound === 1) {
        targetSheetName = "BHMS R1 2026";
      } else {
        targetSheetName = "85% MBBS R3";
      }
    }

    const updatedFilters = {
      ...filters,
      sheetName: targetSheetName,
      category: targetSheetName.includes("AIIMS") ? "ALL CATEGORY" : (filters.category || "OPEN")
    };

    setFilters(updatedFilters);
    localStorage.setItem("ame_neet2026_cutoff_filters", JSON.stringify(updatedFilters));
    fetchSheet(targetSheetName, updatedFilters, newRound);
  };

  // Instant Real-Time Sheet Switcher
  const handleSheetChange = (newSheet) => {
    const isAiimsSheet = newSheet.includes("AIIMS");
    const updated = {
      ...filters,
      sheetName: newSheet,
      category: isAiimsSheet ? "ALL CATEGORY" : (filters.category === "ALL CATEGORY" ? "OPEN" : filters.category)
    };
    setFilters(updated);
    localStorage.setItem("ame_neet2026_cutoff_filters", JSON.stringify(updated));
    fetchSheet(newSheet, updated, selectedRound);
  };

  // Instant Real-Time Category Switcher
  const handleCategoryChange = (newCat) => {
    const updated = { ...filters, category: newCat };
    setFilters(updated);
    localStorage.setItem("ame_neet2026_cutoff_filters", JSON.stringify(updated));
  };

  // Instant Real-Time Gender Switcher
  const handleGenderChange = (newGender) => {
    const updated = { ...filters, gender: newGender };
    setFilters(updated);
    localStorage.setItem("ame_neet2026_cutoff_filters", JSON.stringify(updated));
  };

  // Process and Transform Table & Card Data
  const processTableData = (data, activeFilters, roundNum) => {
    const currentFilters = activeFilters || filters;
    const sheetType = data.type;
    const rNum = roundNum || selectedRound;

    const formatVal = (val) => {
      if (!val || val === "-" || val === "nan" || val === "NaN" || val === "None") return "-";
      const str = String(val).trim();
      if (/^\d+$/.test(str)) {
        return Number(str).toLocaleString();
      }
      return str;
    };

    if (sheetType === "state") {
      const colleges = data.colleges || [];
      const cat = currentFilters.category;
      const gen = (currentFilters.gender || "G").toUpperCase();
      const searchKey = resolveStateCategoryKey(cat, gen);

      const hasCatRank =
        !["OPEN", "HILLY GENERAL", "PWD OPEN", "ORPHAN", "DEF 1", "DEF 2", "DEF 3", "MKB", "I.Q.", "I.Q. MINORITY", "MINORITY OPEN"].includes(cat) &&
        !cat.startsWith("PWD");

      const hasDistrict = colleges.some((c) => Boolean(c.district));

      const head = [
        { title: "Code", subtitle: "" },
        { title: "Institute Name", subtitle: "" }
      ];

      if (hasDistrict) {
        head.push({ title: "District", subtitle: "" });
      }

      head.push(
        { title: "Status", subtitle: "" },
        { title: "Closing AIR", subtitle: `Round ${rNum}` },
        { title: "NEET Score", subtitle: "Closing Score" },
        { title: "State Rank", subtitle: "SML" }
      );

      if (hasCatRank) {
        head.push({ title: "Cat Rank", subtitle: "Category Rank" });
      }

      const cards = [];
      const body = colleges.map((c) => {
        const cutoff = c.cutoffs?.[searchKey] || {};
        const air = cutoff["AIR"];
        const score = cutoff["SCORE"];
        const sml = cutoff["SML"];
        const catRank = cutoff["CAT-RANK"];

        const row = [c.code || "-", c.institute || "-"];
        if (hasDistrict) row.push(c.district || "-");

        row.push(
          c.status || "-",
          formatVal(air),
          formatVal(score),
          formatVal(sml)
        );

        if (hasCatRank) row.push(formatVal(catRank));

        const metrics = [
          { label: `AIR (R${rNum})`, value: formatVal(air), type: "air" },
          { label: "Score", value: formatVal(score), type: "score" },
          { label: "SML", value: formatVal(sml), type: "sml" }
        ];

        if (hasCatRank && catRank && catRank !== "-" && catRank !== "nan") {
          metrics.push({ label: "Cat Rank", value: formatVal(catRank), type: "cat" });
        }

        cards.push({
          code: c.code || "",
          institute: c.institute || "-",
          district: c.district || "",
          status: c.status || "",
          metrics
        });

        return row;
      });

      setTableData({ head, body, cards, activeCategoryBadge: `${searchKey} · Round ${rNum}` });

    } else if (sheetType === "aiims") {
      let records = data.records || [];
      if (currentFilters.category && currentFilters.category !== "ALL CATEGORY") {
        records = records.filter(
          (r) => (r.category || "").toUpperCase() === currentFilters.category.toUpperCase()
        );
      }

      const head = [
        { title: "Institute", subtitle: "" },
        { title: "Category", subtitle: "" },
        { title: "Closing AIR", subtitle: `Round ${rNum}` },
        { title: "NEET Score", subtitle: "Closing Score" }
      ];

      const cards = [];
      const body = records.map((r) => {
        cards.push({
          code: "AIIMS",
          institute: r.institute || "-",
          district: "",
          status: r.category || "General",
          metrics: [
            { label: `AIR (R${rNum})`, value: r.rank ? Number(r.rank).toLocaleString() : "-", type: "air" },
            { label: "Score", value: r.score ? String(r.score) : "-", type: "score" },
            { label: "Category", value: r.category || "Open", type: "sml" }
          ]
        });

        return [
          r.institute || "-",
          r.category || "-",
          r.rank ? Number(r.rank).toLocaleString() : "-",
          r.score ? String(r.score) : "-"
        ];
      });

      setTableData({
        head,
        body,
        cards,
        activeCategoryBadge: `AIIMS · ${currentFilters.category || "ALL"} · Round ${rNum}`
      });

    } else if (sheetType === "deemed") {
      const records = data.records || [];
      const hasState = records.some((r) => Boolean(r.state));
      const hasCat = records.some((r) => Boolean(r.category));

      const head = [
        { title: "#", subtitle: "Sr" },
        { title: "Allotted Institute", subtitle: "" }
      ];

      if (hasState) head.push({ title: "State", subtitle: "" });
      if (hasCat) head.push({ title: "Category", subtitle: "" });

      head.push(
        { title: "Closing AIR", subtitle: `Round ${rNum}` },
        { title: "NEET Score", subtitle: "Closing Score" }
      );

      const cards = [];
      const body = records.map((r, i) => {
        const row = [`#${r.sr_no || i + 1}`, r.institute || "-"];
        if (hasState) row.push(r.state || "-");
        if (hasCat) row.push(r.category || "Open");

        row.push(
          r.rank ? Number(r.rank).toLocaleString() : "-",
          r.score ? String(r.score) : "-"
        );

        const metrics = [
          { label: `AIR (R${rNum})`, value: r.rank ? Number(r.rank).toLocaleString() : "-", type: "air" },
          { label: "Score", value: r.score ? String(r.score) : "-", type: "score" }
        ];
        if (hasState && r.state) {
          metrics.push({ label: "State", value: r.state, type: "sml" });
        }

        cards.push({
          code: `#${r.sr_no || i + 1}`,
          institute: r.institute || "-",
          district: r.state || "",
          status: r.category || "Deemed",
          metrics
        });

        return row;
      });

      setTableData({ head, body, cards, activeCategoryBadge: `Deemed Universities · Round ${rNum}` });

    } else if (sheetType === "aiq_bams") {
      const records = data.records || [];
      const head = [
        { title: "Code", subtitle: "" },
        { title: "Institute Name", subtitle: "" },
        { title: "District", subtitle: "" },
        { title: "Status", subtitle: "" },
        { title: "Closing AIR", subtitle: `Round ${rNum}` },
        { title: "NEET Score", subtitle: "Closing Score" },
        { title: "AIQ State Rank", subtitle: "State Merit" }
      ];

      const cards = [];
      const body = records.map((r) => {
        const cleanAir = r.rank && r.rank !== "-" ? (typeof r.rank === "number" ? r.rank.toLocaleString() : String(r.rank)) : "-";
        const cleanScore = r.score && r.score !== "-" ? String(r.score) : "-";
        const cleanStateRank = r.aiq_state_rank && r.aiq_state_rank !== "-" ? (typeof r.aiq_state_rank === "number" ? r.aiq_state_rank.toLocaleString() : String(r.aiq_state_rank)) : "-";

        cards.push({
          code: r.code ? String(r.code) : "",
          institute: r.institute || "-",
          district: r.district || "",
          status: r.status || "Private",
          metrics: [
            { label: `AIR (R${rNum})`, value: cleanAir, type: "air" },
            { label: "Score", value: cleanScore, type: "score" },
            { label: "AIQ Rank", value: cleanStateRank, type: "sml" }
          ]
        });

        return [
          r.code || "-",
          r.institute || "-",
          r.district || "-",
          r.status || "Private",
          cleanAir,
          cleanScore,
          cleanStateRank
        ];
      });

      setTableData({ head, body, cards, activeCategoryBadge: `15% AIQ Private AYUSH · Round ${rNum}` });

    } else if (sheetType === "kea") {
      const records = data.records || [];
      const head = [
        { title: "Code", subtitle: "" },
        { title: "Institute Name", subtitle: "" },
        { title: "Fees", subtitle: "" },
        { title: "Closing AIR", subtitle: `Round ${rNum}` },
        { title: "NEET Score", subtitle: "Closing Score" }
      ];

      const cards = [];
      const body = records.map((r) => {
        cards.push({
          code: r.code || "",
          institute: r.institute || "-",
          district: "",
          status: "KEA",
          metrics: [
            { label: `AIR (R${rNum})`, value: r.rank ? Number(r.rank).toLocaleString() : "-", type: "air" },
            { label: "Score", value: r.score ? String(r.score) : "-", type: "score" },
            { label: "Fees", value: r.fees ? `₹${r.fees}` : "-", type: "sml" }
          ]
        });

        return [
          r.code || "-",
          r.institute || "-",
          r.fees ? `₹${r.fees}` : "-",
          r.rank ? Number(r.rank).toLocaleString() : "-",
          r.score ? String(r.score) : "-"
        ];
      });

      setTableData({ head, body, cards, activeCategoryBadge: `Karnataka KEA · Round ${rNum}` });
    }
  };

  const currentSheetDetail = metadata.sheetDetails?.find((s) => s.name === filters.sheetName);
  const isStateQuota = currentSheetDetail?.type === "state";
  const isAiims = currentSheetDetail?.type === "aiims";

  // Filter sheets visible in active round and active course
  const visibleSheets = useMemo(() => {
    return (metadata.sheetDetails || []).filter((s) => (s.round || 3) === selectedRound);
  }, [metadata.sheetDetails, selectedRound]);

  const displayedSheets = useMemo(() => {
    if (selectedCourseFilter === "ALL") return visibleSheets;
    return visibleSheets.filter((s) => (s.course || "").toUpperCase() === selectedCourseFilter.toUpperCase());
  }, [visibleSheets, selectedCourseFilter]);

  const groupedSheets = useMemo(() => {
    const list = displayedSheets.length > 0 ? displayedSheets : visibleSheets;
    return list.reduce((acc, s) => {
      const grp = s.group || "Other Quotas";
      if (!acc[grp]) acc[grp] = [];
      acc[grp].push(s);
      return acc;
    }, {});
  }, [displayedSheets, visibleSheets]);

  // Client-side search filtering
  const filteredBody = useMemo(() => {
    if (!searchTerm) return tableData.body || [];
    const s = searchTerm.toLowerCase();
    return (tableData.body || []).filter((row) =>
      row.some((cell) => String(cell).toLowerCase().includes(s))
    );
  }, [tableData.body, searchTerm]);

  const filteredCards = useMemo(() => {
    if (!searchTerm) return tableData.cards || [];
    const s = searchTerm.toLowerCase();
    return (tableData.cards || []).filter(
      (c) =>
        (c.institute && c.institute.toLowerCase().includes(s)) ||
        (c.code && c.code.toLowerCase().includes(s)) ||
        (c.district && c.district.toLowerCase().includes(s)) ||
        (c.status && c.status.toLowerCase().includes(s))
    );
  }, [tableData.cards, searchTerm]);

  const isNoGenderCategory = useMemo(() => {
    const found = SPECIAL_CATEGORIES.find((i) => i.id === filters.category);
    return found?.isNoGender || false;
  }, [filters.category]);

  const isSpecialCategorySelected = useMemo(() => {
    return SPECIAL_CATEGORIES.some((c) => c.id === filters.category);
  }, [filters.category]);

  return (
    <div className="ce-root">
      {/* ── TOP STICKY NAVBAR ────────────────────────────────────────── */}
      <header className="ce-top-navbar">
        <div className="ce-nav-left">
          <button onClick={() => navigate(-1)} className="ce-back-btn">
            <ArrowLeft size={13} /> Back
          </button>
          <div className="ce-brand-badge">
            <img src="/landing-assets/images/branding/logo1.png" alt="AME" className="ce-brand-logo" />
            <div className="ce-brand-titles">
              <span className="ce-brand-name">Admissions Made Easy</span>
              <span className="ce-brand-tagline">⭐ Trusted Since 2011 · 14+ Years</span>
            </div>
          </div>
        </div>

        {/* ── INTERACTIVE 3-ROUND SWITCHER (R3, R2, R1) ───────────────── */}
        <div className="ce-round-tabs-header">
          <button
            type="button"
            className={`ce-round-tab ${selectedRound === 3 ? "active-r3" : ""}`}
            onClick={() => handleRoundChange(3)}
          >
            <span className="ce-pulse-dot" />
            Round 3 <span className="ce-badge-pill">LATEST</span>
          </button>

          <button
            type="button"
            className={`ce-round-tab ${selectedRound === 2 ? "active-r2" : ""}`}
            onClick={() => handleRoundChange(2)}
          >
            Round 2
          </button>

          <button
            type="button"
            className={`ce-round-tab ${selectedRound === 1 ? "active-r1" : ""}`}
            onClick={() => handleRoundChange(1)}
          >
            Round 1
          </button>
        </div>

        <div className="ce-nav-right">
          <button
            type="button"
            onClick={() => setShowCelebration(true)}
            className="ce-btn-celebrate-replay"
            title="Watch 861+ Admissions Milestone"
          >
            <span className="ce-btn-text-desktop">🎉 861+ Achievers</span>
            <span className="ce-btn-text-mobile">🎉 861+</span>
          </button>
          <a href={`tel:${PHONE}`} className="ce-btn-phone">
            <Phone size={12} /> {PHONE}
          </a>
          <button onClick={() => setShowBookingModal(true)} className="ce-btn-counselling">
            <span className="ce-btn-text-desktop">Book Free Counselling</span>
            <span className="ce-btn-text-mobile"><Phone size={11} /> Book</span>
          </button>
        </div>
      </header>

      {/* ── MOBILE DEDICATED ROUND BAR (VISIBLE ONLY ON MOBILE < 768px) ── */}
      <div className="ce-mobile-round-bar">
        <button
          type="button"
          className={`ce-round-tab ${selectedRound === 3 ? "active-r3" : ""}`}
          onClick={() => handleRoundChange(3)}
        >
          <span className="ce-pulse-dot" />
          Round 3 <span className="ce-badge-pill">LATEST</span>
        </button>

        <button
          type="button"
          className={`ce-round-tab ${selectedRound === 2 ? "active-r2" : ""}`}
          onClick={() => handleRoundChange(2)}
        >
          Round 2
        </button>

        <button
          type="button"
          className={`ce-round-tab ${selectedRound === 1 ? "active-r1" : ""}`}
          onClick={() => handleRoundChange(1)}
        >
          Round 1
        </button>
      </div>

      {/* ── SLIM MILESTONE STRIP ────────────────────────────────────── */}
      <div className="ce-ticker-strip">
        <div className="ce-ticker-track">
          <span className="ce-ticker-item">🏆 861+ MBBS Seats Secured in NEET 2026 (Round 1, 2 &amp; Round 3)</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">📍 Branch Offices: Latur · Pune · Solapur · Kolhapur · Nanded</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🏛️ Since 2011 — 14+ Years of Expert Medical Admission Mentorship</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🎯 SML &amp; AIR Precision Preference Filling — Zero Seat Wastage</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🏆 861+ MBBS Seats Secured in NEET 2026 (Round 1, 2 &amp; Round 3)</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">📍 Branch Offices: Latur · Pune · Solapur · Kolhapur · Nanded</span>
        </div>
      </div>

      {/* ── PHYSICAL HANDBOOK STRIP ──────────────────────────────────── */}
      <div className="ce-handbook-strip">
        <span>📖 NEET UG 2026 फिजिकल हँडबुक हवे असल्यास —</span>
        <button
          onClick={() => {
            const msg = "Hello Admissions Made Easy, I want to order the physical NEET 2026 Admission Process Handbook.";
            window.open(`https://wa.me/919970809003?text=${encodeURIComponent(msg)}`, "_blank");
          }}
          className="ce-handbook-link"
        >
          येथे क्लिक करा (WhatsApp Order)
        </button>
      </div>

      {/* ── MAIN WORKSPACE ──────────────────────────────────────────── */}
      <main className="ce-workspace">
        {/* ── CONTROL HUB (INSTANT REAL-TIME FILTERING) ────────────────── */}
        <div className="ce-control-hub">
          {/* Row 1: Course Stream Tabs + Quota Select + Search */}
          <div className="ce-hub-row-1">
            <div className="ce-course-tabs">
              {[
                { id: "ALL", label: "All Courses" },
                { id: "MBBS", label: "🩺 MBBS" },
                { id: "BDS", label: "🦷 BDS" },
                { id: "BAMS", label: "🌿 BAMS" },
                { id: "BHMS", label: "🌸 BHMS" }
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`ce-course-tab ${
                    selectedCourseFilter === c.id
                      ? c.id === "BAMS" || c.id === "BHMS"
                        ? "active ayush"
                        : "active"
                      : ""
                  }`}
                  onClick={() => handleCourseFilterChange(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="ce-quota-selector-wrap">
              <select
                className="ce-quota-select"
                value={filters.sheetName}
                onChange={(e) => handleSheetChange(e.target.value)}
              >
                {Object.entries(groupedSheets).map(([groupTitle, sheets]) => (
                  <optgroup key={groupTitle} label={groupTitle}>
                    {sheets.map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.title || s.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="ce-search-box">
              <Search size={14} className="ce-search-icon" />
              <input
                type="text"
                placeholder="Search college, code, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ce-search-input"
              />
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm("")} className="ce-search-clear">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Row 2: Instant Category Strip & Gender Toggle (For State & AIIMS) */}
          {(isStateQuota || isAiims) && (
            <div className="ce-hub-row-2">
              <div className="ce-cat-chips-scroll">
                {isStateQuota && (
                  <>
                    {PRIMARY_CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryChange(cat.id)}
                        className={`ce-cat-chip ${filters.category === cat.id ? "active" : ""}`}
                      >
                        {cat.label}
                      </button>
                    ))}

                    <select
                      className={`ce-more-cats-dropdown ${isSpecialCategorySelected ? "active" : ""}`}
                      value={isSpecialCategorySelected ? filters.category : ""}
                      onChange={(e) => {
                        if (e.target.value) handleCategoryChange(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        + More Quotas (DEF / PWD / MKB)
                      </option>
                      {SPECIAL_CATEGORIES.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {isAiims && (
                  <>
                    {["ALL CATEGORY", "OPEN", "OBC", "EWS", "SC", "ST", "OPEN PWD", "OBC PWD", "EWS PWD", "SC PWD", "ST PWD"].map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => handleCategoryChange(cat)}
                        className={`ce-cat-chip ${filters.category === cat ? "active" : ""}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </>
                )}
              </div>

              {isStateQuota && !isNoGenderCategory && (
                <div className="ce-gender-toggle-group">
                  <button
                    type="button"
                    onClick={() => handleGenderChange("G")}
                    className={`ce-gender-btn ${filters.gender === "G" ? "active male" : ""}`}
                  >
                    General
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGenderChange("W")}
                    className={`ce-gender-btn ${filters.gender === "W" ? "active female" : ""}`}
                  >
                    Women (30%)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── STATUS BAR ─────────────────────────────────────────────── */}
        <div className="ce-table-status-bar">
          <span className="ce-badge-live">
            <Sparkles size={12} /> {tableData.activeCategoryBadge || filters.sheetName}
          </span>
          <span className="ce-colleges-count">
            {filteredCards.length} {filteredCards.length === 1 ? "college" : "colleges"} listed
          </span>
        </div>

        {/* ── DESKTOP DATA TABLE VIEW (≥ 768px) ───────────────────────── */}
        <div className="ce-table-card">
          <div className="ce-watermark-layer" aria-hidden="true" />
          <div className="ce-table-wrapper">
            {loading && (
              <div className="ce-loading-overlay">
                <div className="ce-spinner" />
                <span className="text-xs font-bold text-amber-400">Loading Cutoffs...</span>
              </div>
            )}

            {filteredBody.length > 0 ? (
              <table className="ce-data-table">
                <thead>
                  <tr>
                    {tableData.head.map((h, i) => (
                      <th key={i}>
                        {h.title}
                        {h.subtitle && h.subtitle !== "nan" && h.subtitle !== "--" && (
                          <span className="ce-th-sub">{h.subtitle}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredBody.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => {
                        const h = tableData.head[ci];
                        const titleLower = (h?.title || "").toLowerCase();

                        // Visual styling based on column
                        if (titleLower === "code" || titleLower === "#") {
                          return (
                            <td key={ci}>
                              <span className="ce-code-chip">{cell}</span>
                            </td>
                          );
                        }

                        if (titleLower.includes("institute")) {
                          return (
                            <td key={ci}>
                              <span className="ce-inst-title">{cell}</span>
                            </td>
                          );
                        }

                        if (titleLower === "status") {
                          const sLower = String(cell).toLowerCase();
                          const cls = sLower.includes("govt") ? "govt" : sLower.includes("deemed") ? "deemed" : "pvt";
                          return (
                            <td key={ci}>
                              <span className={`ce-status-chip ${cls}`}>{cell}</span>
                            </td>
                          );
                        }

                        if (titleLower.includes("air") || titleLower.includes("rank")) {
                          return (
                            <td key={ci}>
                              <span className="ce-val-air">{cell}</span>
                            </td>
                          );
                        }

                        if (titleLower.includes("score")) {
                          return (
                            <td key={ci}>
                              <span className="ce-val-score">{cell}</span>
                            </td>
                          );
                        }

                        if (titleLower.includes("state rank") || titleLower.includes("sml")) {
                          return (
                            <td key={ci}>
                              <span className="ce-val-sml">{cell}</span>
                            </td>
                          );
                        }

                        if (titleLower.includes("cat")) {
                          return (
                            <td key={ci}>
                              <span className="ce-val-cat">{cell}</span>
                            </td>
                          );
                        }

                        return <td key={ci}>{cell}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="ce-empty-state">
                <p>
                  {searchTerm
                    ? `No colleges matching "${searchTerm}". Try another search term.`
                    : error || "No cutoff records available for this selection."}
                </p>
              </div>
            )}
          </div>
          {/* Table Watermark Attribution Bar */}
          <div className="ce-table-watermark-bar">
            <span>© Admissions Made Easy · Official NEET 2026 Cutoff Directory</span>
            <span>Helpline: +91 9309553235 · admissionsmadeeasy.in</span>
          </div>
        </div>

        {/* ── DEDICATED MOBILE CARDS VIEW (< 768px) ────────────────────── */}
        <div className="ce-mobile-cards-view">
          <div className="ce-watermark-layer" aria-hidden="true" />
          {filteredCards.length > 0 ? (
            filteredCards.map((card, idx) => (
              <div className="ce-mobile-card" key={idx}>
                <div className="ce-mcard-header">
                  <div className="flex items-center gap-1 flex-wrap">
                    {card.code && <span className="ce-code-chip">{card.code}</span>}
                    {card.status && (
                      <span
                        className={`ce-status-chip ${
                          card.status.toLowerCase().includes("govt")
                            ? "govt"
                            : card.status.toLowerCase().includes("deemed")
                            ? "deemed"
                            : "pvt"
                        }`}
                      >
                        {card.status}
                      </span>
                    )}
                  </div>
                  {card.district && (
                    <span className="ce-tag-district">
                      <MapPin size={9} /> {card.district}
                    </span>
                  )}
                </div>

                <div className="ce-mcard-name">{card.institute}</div>

                <div className={`ce-mcard-grid cols-${card.metrics.length}`}>
                  {card.metrics.map((m, mi) => (
                    <div className="ce-mcard-tile" key={mi}>
                      <span className="ce-mcard-lbl">{m.label}</span>
                      <span className={`ce-mcard-val ${m.type}`}>{m.value}</span>
                    </div>
                  ))}
                </div>

                {/* Mobile Card Subtle Anti-Screenshot Stamp */}
                <div className="ce-mcard-ame-stamp">
                  <span>Admissions Made Easy</span>
                  <span>Helpline: 9309553235</span>
                </div>
              </div>
            ))
          ) : (
            <div className="ce-empty-state">
              <p>
                {searchTerm
                  ? `No colleges matching "${searchTerm}".`
                  : error || "No cutoff records available for this selection."}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Booking Modal */}
      <CelebrationIntroModal
        isOpen={showCelebration}
        onClose={handleCelebrationClose}
      />

      {showBookingModal && (
        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
        />
      )}
    </div>
  );
}
